import React, { useState, useEffect } from 'react';
import { Modal, Button, Space, Typography, message, Select, Divider, Alert } from 'antd';
import { MenuOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { saveUserSettings } from '../../redux/slieces/userSettingsSlice';
import { saveOrganizationSettings } from '../../redux/slieces/organizationSettingsSlice';
import { getUsersInOrganisation } from '../../redux/slieces/userSlice';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './WeekPlan.module.css';

const { Title, Text } = Typography;

// Sortable Item Component
const SortableItem = ({ id, worker }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
    padding: '12px 16px',
    marginBottom: '8px',
    background: '#fff',
    border: '1px solid #d9d9d9',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <MenuOutlined style={{ color: '#999' }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500 }}>{worker.name}</div>
        {worker.position && (
          <div style={{ fontSize: '12px', color: '#666' }}>{worker.position}</div>
        )}
      </div>
    </div>
  );
};

const WorkerOrderModal = ({ visible, onClose, isOrganizationWide = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get data from Redux
  const { users } = useSelector((state) => state.userData.users);
  const { settings: userSettings } = useSelector((state) => state.userSettings);
  const { settings: organizationSettings } = useSelector((state) => state.organizationSettings);

  // Use the appropriate settings based on mode
  const currentSettings = isOrganizationWide ? organizationSettings : userSettings;

  // State for automatic sorting
  const [sortBy, setSortBy] = useState(currentSettings?.workerSortBy || 'Name');
  const [sortOrder, setSortOrder] = useState(currentSettings?.workerSortOrder || 'Asc');

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch users when modal opens
  useEffect(() => {
    if (visible) {
      dispatch(getUsersInOrganisation());
    }
  }, [visible, dispatch]);

  // Update sort state when settings change
  useEffect(() => {
    if (currentSettings) {
      setSortBy(currentSettings.workerSortBy || 'Name');
      setSortOrder(currentSettings.workerSortOrder || 'Asc');
    }
  }, [currentSettings]);

  // Load workers when modal opens
  useEffect(() => {
    if (visible && users && users.length > 0) {
      // Create worker list from users
      const workerList = users.map((user) => ({
        id: user.id,
        userId: user.id,
        name: user.name || `${user.firstName} ${user.surname}`,
        firstName: user.firstName,
        surname: user.surname,
        position: user.position,
        email: user.email,
      }));

      // If custom order exists, apply it
      if (currentSettings.customWorkerOrder && currentSettings.customWorkerOrder.length > 0) {
        const orderArray = typeof currentSettings.customWorkerOrder === 'string'
          ? JSON.parse(currentSettings.customWorkerOrder)
          : currentSettings.customWorkerOrder;

        const positionMap = new Map();
        orderArray.forEach((id, index) => {
          positionMap.set(id, index);
        });

        const sorted = [...workerList].sort((a, b) => {
          const posA = positionMap.has(a.id) ? positionMap.get(a.id) : Infinity;
          const posB = positionMap.has(b.id) ? positionMap.get(b.id) : Infinity;
          return posA - posB;
        });

        setWorkers(sorted);
      } else {
        setWorkers(workerList);
      }
    }
  }, [visible, users, currentSettings]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setWorkers((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Handle automatic sorting changes (save immediately)
  const handleSortByChange = async (value) => {
    try {
      setSortBy(value);
      console.log("💾 Saving sortBy:", value);

      const updatedSettings = isOrganizationWide
        ? { ...organizationSettings, workerSortBy: value }
        : { ...userSettings, workerSortBy: value };

      if (isOrganizationWide) {
        await dispatch(saveOrganizationSettings(updatedSettings)).unwrap();
        console.log("✅ Organization sortBy saved");
      } else {
        await dispatch(saveUserSettings(updatedSettings)).unwrap();
        console.log("✅ User sortBy saved");
      }

      message.success(t('settingsSaved'));
    } catch (error) {
      console.error('❌ Error saving sortBy:', error);
      message.error(t('errorSavingSettings'));
    }
  };

  const handleSortOrderChange = async (value) => {
    try {
      setSortOrder(value);
      console.log("💾 Saving sortOrder:", value);

      const updatedSettings = isOrganizationWide
        ? { ...organizationSettings, workerSortOrder: value }
        : { ...userSettings, workerSortOrder: value };

      if (isOrganizationWide) {
        await dispatch(saveOrganizationSettings(updatedSettings)).unwrap();
        console.log("✅ Organization sortOrder saved");
      } else {
        await dispatch(saveUserSettings(updatedSettings)).unwrap();
        console.log("✅ User sortOrder saved");
      }

      message.success(t('settingsSaved'));
    } catch (error) {
      console.error('❌ Error saving sortOrder:', error);
      message.error(t('errorSavingSettings'));
    }
  };

  // Save custom order
  const handleSave = async () => {
    try {
      setLoading(true);

      // Create array of user IDs in current order
      const customOrder = workers.map((worker) => worker.id);
      console.log("💾 Saving worker order:", customOrder);

      if (isOrganizationWide) {
        // Save organization-wide settings
        const updatedSettings = {
          ...organizationSettings,
          customWorkerOrder: JSON.stringify(customOrder),
        };
        console.log("📤 Saving organization settings:", updatedSettings);
        await dispatch(saveOrganizationSettings(updatedSettings)).unwrap();
        console.log("✅ Organization settings saved successfully");
      } else {
        // Save user-specific settings
        const updatedSettings = {
          ...userSettings,
          customWorkerOrder: JSON.stringify(customOrder),
        };
        console.log("📤 Saving user settings:", updatedSettings);
        await dispatch(saveUserSettings(updatedSettings)).unwrap();
        console.log("✅ User settings saved successfully");
      }

      message.success(t('customWorkerOrderSaved'));

      // Notify parent to refresh data
      console.log("🔄 Closing modal - parent components should refetch data");
      onClose();
    } catch (error) {
      console.error('❌ Error saving custom worker order:', error);
      message.error(t('errorSavingCustomOrder'));
    } finally {
      setLoading(false);
    }
  };

  // Clear custom order and use automatic sorting
  const handleClearCustomOrder = async () => {
    try {
      setLoading(true);

      if (isOrganizationWide) {
        // Clear organization-wide custom order
        const updatedSettings = {
          ...organizationSettings,
          customWorkerOrder: null,
        };
        await dispatch(saveOrganizationSettings(updatedSettings)).unwrap();
      } else {
        // Clear user-specific custom order
        const updatedSettings = {
          ...userSettings,
          customWorkerOrder: null,
        };
        await dispatch(saveUserSettings(updatedSettings)).unwrap();
      }

      message.success(t('customOrderCleared'));
      onClose();
    } catch (error) {
      console.error('Error clearing custom order:', error);
      message.error(t('errorClearingCustomOrder'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <MenuOutlined />
          {t('customizeWorkerOrder')}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="clear" onClick={handleClearCustomOrder} loading={loading}>
          {t('clearCustomOrder')}
        </Button>,
        <Button key="cancel" onClick={onClose}>
          {t('cancel')}
        </Button>,
        <Button key="save" type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
          {t('saveOrder')}
        </Button>,
      ]}
    >
      {/* Section 1: Automatic Sorting */}
      <div style={{ marginBottom: '24px' }}>
        <Typography.Title level={5} style={{ marginBottom: '12px' }}>
          {t('automaticSorting')}
        </Typography.Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
          {t('automaticSortingDescription')}
        </Text>

        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              {t('sortWorkersBy')}:
            </Text>
            <Select
              style={{ width: '100%' }}
              value={sortBy}
              onChange={handleSortByChange}
              options={[
                { value: 'Name', label: t('name') },
                { value: 'FirstName', label: t('firstName') },
                { value: 'Surname', label: t('surname') },
                { value: 'Position', label: t('position') },
              ]}
            />
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              {t('sortOrder')}:
            </Text>
            <Select
              style={{ width: '100%' }}
              value={sortOrder}
              onChange={handleSortOrderChange}
              options={[
                { value: 'Asc', label: t('ascending') },
                { value: 'Desc', label: t('descending') },
              ]}
            />
          </div>
        </Space>
      </div>

      <Divider />

      {/* Section 2: Custom Worker Order */}
      <div>
        <Typography.Title level={5} style={{ marginBottom: '12px' }}>
          {t('customWorkerOrder')}
        </Typography.Title>

        {currentSettings?.customWorkerOrder && (
          <Alert
            message={t('customOrderActive')}
            description={t('customOrderPrecedenceInfo')}
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
          {t('dragToReorderWorkers')}
        </Text>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={workers.map((w) => w.id)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {workers.map((worker) => (
                <SortableItem key={worker.id} id={worker.id} worker={worker} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {workers.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            {t('noWorkersAvailable')}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WorkerOrderModal;
